"use client"

import { useForm, type SubmitHandler } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import type { components } from "umbraco"
import { postEggedPlusContactSubmit } from "api"
import {
  FormShell,
  FormTextField,
  FormCheckboxField,
  FieldRow,
  FieldCol,
  useFormSubmit,
  checkBooleanResponse,
  validateRecipients,
  RecipientsConfigError,
  getShellLabels,
  type FormLanguage,
} from "../../common/forms"
import { createSchema, type FormValues, defaultValues } from "./formSchema"

type ContactUsFormData = components["schemas"]["ContactUsFormPropertiesModel"]

interface Labels {
  fullName: string
  phone: string
  email: string
  company: string
  message: string
}

/* Field labels only — the success/error/submit "shell" copy comes from the
   shared getShellLabels (single source of truth across all four forms), which
   carries the EGDE-260 spec strings. */
function getLabels(locale: string): Labels {
  if (locale === "en") {
    return {
      fullName: "Full Name",
      phone: "Phone",
      email: "Email",
      company: "Company / Organization",
      message: "What would you like to write to us?",
    }
  }
  return {
    fullName: "שם מלא",
    phone: "טלפון",
    email: 'דוא"ל',
    company: "שם חברה/ארגון",
    message: "מה תרצו לכתוב לנו?",
  }
}

interface Props {
  data: ContactUsFormData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function ContactUsForm({ data, locale }: Props) {
  const labels = getLabels(locale)
  const standardRecipients = data.standardRecipients ?? []
  const businessRecipients = data.businessRecipients ?? []
  // EGDE-537: the CRM division comes from the block instance (per page /
  // context, set by editors); empty falls back to the Egged Plus default.
  const division = data.division?.trim() || "חוויות ותוכן"
  const showCheckbox = data.showCheckbox ?? false
  const checkboxRequired = showCheckbox && (data.checkboxRequired ?? false)

  const schema = createSchema(locale as FormLanguage, checkboxRequired)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onBlur",
  })

  const cultureLocale =
    locale === "he" ? "he-IL" : locale === "en" ? "en-US" : locale

  // EGDE-201: the backend forwards Contact Us leads to Zoho CRM, so the UTM
  // params ride along on the regular submit (camelCase API names). `utmMedia`
  // is the CRM field name; URLs may use the standard `utm_medium`, so accept
  // either.
  // EGDE-526: campaign params live on the LANDING url, not the contact page —
  // when the current url has none, fall back to the session capture the root
  // layout stored (key "eggedUtmParams", snake_case) so the lead still
  // carries them.
  function readUtmParams() {
    if (typeof window === "undefined") {
      return { utmSource: "", utmMedia: "", utmCampaign: "" }
    }
    const p = new URLSearchParams(window.location.search)
    const fromUrl = {
      utmSource: p.get("utm_source") ?? "",
      utmMedia: p.get("utm_media") ?? p.get("utm_medium") ?? "",
      utmCampaign: p.get("utm_campaign") ?? "",
    }
    if (fromUrl.utmSource || fromUrl.utmMedia || fromUrl.utmCampaign) {
      return fromUrl
    }
    try {
      const stored = sessionStorage.getItem("eggedUtmParams")
      if (stored) {
        const s = JSON.parse(stored) as {
          utm_source?: string
          utm_media?: string
          utm_campaign?: string
        }
        return {
          utmSource: s.utm_source ?? "",
          utmMedia: s.utm_media ?? "",
          utmCampaign: s.utm_campaign ?? "",
        }
      }
    } catch {
      // sessionStorage unavailable / corrupt — send empty UTMs
    }
    return fromUrl
  }

  const { state, isPending, submit, retry } = useFormSubmit<FormValues>({
    mutationFn: async (formData) => {
      // EGDE-201: single request — the backend forwards the lead to Zoho CRM
      // server-side (EGDE-200), so the client must NOT call Zoho itself. The
      // hidden CRM fields (division + UTMs) ride along on the regular submit.
      return checkBooleanResponse(
        await postEggedPlusContactSubmit(
          {
            fullName: formData.fullName,
            email: formData.email || "",
            phone: formData.phone,
            company: formData.company || "",
            message: formData.message || "",
            referrerUrl: window.location.href,
            businessRecipients,
            standardRecipients,
            attachment: { fileName: "", base64Content: "" },
            // EGDE-576: only send consent when the checkbox is actually
            // rendered — a hidden checkbox must not arrive as consent given.
            // `undefined` is dropped from the JSON body (same as EGDE-445).
            ...(showCheckbox
              ? { consent: Boolean(formData.checkbox) }
              : {}),
            // EGDE-526: `division` is hidden from the user but required by
            // the CRM spec. EGDE-537: editor-configured per block instance.
            division,
            ...readUtmParams(),
          } as Parameters<typeof postEggedPlusContactSubmit>[0],
          cultureLocale,
        ),
      )
    },
    onReset: reset,
  })

  if (!validateRecipients(standardRecipients, businessRecipients)) {
    return <RecipientsConfigError formName="ContactUsForm" />
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => submit(formData)

  return (
    <FormShell
      title={data.title}
      subtitle={data.subtitle}
      labels={getShellLabels(locale)}
      state={state}
      isPending={isPending}
      onSubmit={handleSubmit(onSubmit)}
      onRetry={retry}
      footer={
        showCheckbox ? (
          <FormCheckboxField
            control={control}
            name="checkbox"
            markup={data.checkboxText?.markup}
            required={checkboxRequired}
            error={errors.checkbox?.message}
          />
        ) : null
      }
    >
      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="fullName"
            label={labels.fullName}
            required
            autoComplete="name"
            error={errors.fullName?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="phone"
            label={labels.phone}
            required
            type="tel"
            autoComplete="tel"
            error={errors.phone?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="email"
            label={labels.email}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="company"
            label={labels.company}
            autoComplete="organization"
            error={errors.company?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldCol>
        <FormTextField
          control={control}
          name="message"
          label={labels.message}
          maxLength={300}
          multiline
          rows={1}
          error={errors.message?.message}
        />
      </FieldCol>
    </FormShell>
  )
}
